import { escapeHtmlAttribute, iconMaskStyle, lucideIconName } from "./icon-html"

export const BADGE_COLORS = [
	"gray",
	"blue",
	"green",
	"yellow",
	"orange",
	"red",
	"purple",
	"white",
	"surface",
	"white-destructive",
	"surface-destructive",
] as const
export const BADGE_SIZES = ["xs", "sm", "md", "lg"] as const
export const BADGE_SHAPES = ["rounded", "pill"] as const

export type BadgeColor = (typeof BADGE_COLORS)[number]
export type BadgeSize = (typeof BADGE_SIZES)[number]
export type BadgeShape = (typeof BADGE_SHAPES)[number]

export const DEFAULT_BADGE_COLOR: BadgeColor = "gray"
export const DEFAULT_BADGE_SIZE: BadgeSize = "md"
export const DEFAULT_BADGE_SHAPE: BadgeShape = "rounded"

export interface BadgeAttributes {
	color: BadgeColor
	size: BadgeSize
	shape: BadgeShape
	icon: string | null
	stroke: boolean
	disabled: boolean
}

const pick = <T extends string>(
	allowed: readonly T[],
	value: unknown,
	fallback: T,
): T => {
	const normalized = typeof value === "string" ? value.trim().toLowerCase() : ""
	return (allowed as readonly string[]).includes(normalized)
		? (normalized as T)
		: fallback
}

// MDX writes a bare `stroke` for true; the HTML carries "true" or "false".
export const readBadgeFlag = (value: unknown): boolean =>
	value === true || value === "" || value === "true"

export function normalizeBadgeAttributes(
	raw: Partial<Record<keyof BadgeAttributes, unknown>>,
): BadgeAttributes {
	return {
		color: pick(BADGE_COLORS, raw.color, DEFAULT_BADGE_COLOR),
		size: pick(BADGE_SIZES, raw.size, DEFAULT_BADGE_SIZE),
		shape: pick(BADGE_SHAPES, raw.shape, DEFAULT_BADGE_SHAPE),
		icon: lucideIconName(raw.icon),
		stroke: readBadgeFlag(raw.stroke),
		disabled: readBadgeFlag(raw.disabled),
	}
}

/** The attributes of the badge element, the same ones the editor renders. */
export function badgeElementAttributes(
	attributes: BadgeAttributes,
): Record<string, string> {
	const style = iconMaskStyle(attributes.icon)
	return {
		"data-type": "badge",
		"data-color": attributes.color,
		"data-size": attributes.size,
		"data-shape": attributes.shape,
		"data-stroke": String(attributes.stroke),
		"data-disabled": String(attributes.disabled),
		...(attributes.icon && style
			? { "data-icon": attributes.icon, style }
			: {}),
		class: "pl-badge",
	}
}

export function badgeOpenTag(attributes: BadgeAttributes): string {
	const serialized = Object.entries(badgeElementAttributes(attributes))
		.map(([key, value]) => `${key}="${escapeHtmlAttribute(value)}"`)
		.join(" ")
	return `<span ${serialized}>`
}

export function badgeHtml(
	raw: Partial<Record<keyof BadgeAttributes, unknown>>,
	innerHtml: string,
): string {
	return `${badgeOpenTag(normalizeBadgeAttributes(raw))}${innerHtml}</span>`
}
